<template>
  <div class="p-6 max-w-6xl mx-auto space-y-6">
    <el-card shadow="hover">
      <template #header>
        <div class="flex items-start md:items-center justify-between flex-col md:flex-row gap-3">
          <h2 class="text-lg font-semibold">{{ isMobile() ? '融资租赁' : '融资租赁 / 分期付款' }}</h2>
        </div>
      </template>

      <el-alert type="info" :closable="false" class="mb-3" title="用途：按等额本息计算租金/分期付款金额，可叠加每期固定费用，生成还款/租金计划表。" />
      <FormulaPanel
        title="等额本息（租金）"
        :formulas="[String.raw`PMT = \\dfrac{PV\\cdot r(1+r)^n}{(1+r)^n-1}`]"
        :variables="[{symbol:'PV',label:'本金'},{symbol:'r',label:'每期利率'},{symbol:'n',label:'期数'},{symbol:'PMT',label:'每期租金'}]"
      />
  <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <el-form label-position="top" class="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <el-form-item :label="`设备价格（${symbol}）`">
            <el-input v-model.number="amountUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PV）</div>
          </el-form-item>
          <el-form-item label="每期利率(%)">
            <el-input v-model.number="form.periodRatePct" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写r）</div>
          </el-form-item>
          <el-form-item label="期数">
            <el-input v-model.number="form.periods" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写n）</div>
          </el-form-item>
          <el-form-item :label="`每期费用（${symbol}）`">
            <el-input v-model.number="periodicFeeUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写f）</div>
          </el-form-item>
        </el-form>
  <div class="md:col-span-1 flex gap-3 md:justify-end flex-wrap">
          <el-button type="primary" @click="onCalc">计算</el-button>
          <el-button @click="onReset">重置</el-button>
        </div>
      </div>

      <el-collapse-transition>
        <div v-if="result" class="mt-4 space-y-4">
  <el-descriptions title="结果" :column="2" border>
    <el-descriptions-item :label="`每期租金/还款（${symbol}）`">{{ d2(result.pmt) }}</el-descriptions-item>
    <el-descriptions-item :label="`总利息（${symbol}）`">{{ d2(result.totalInterest) }}</el-descriptions-item>
    <el-descriptions-item :label="`总支付（${symbol}）`">{{ d2(result.totalPayment) }}</el-descriptions-item>
        </el-descriptions>

        <el-table :data="displayRows" height="300" size="small">
          <el-table-column prop="period" label="#" width="60"/>
          <el-table-column prop="payment" label="当期支付"/>
          <el-table-column prop="principal" label="本金"/>
          <el-table-column prop="interest" label="利息"/>
          <el-table-column prop="balance" label="剩余本金"/>
        </el-table>
        </div>
      </el-collapse-transition>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { pmtEqualPI, scheduleEqualPI } from '@/lib/loanEngine'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
import { useCurrency } from '@/composables/useCurrency'
function isMobile() {
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
const form = ref({ amount: 100000, periodRatePct: 1, periods: 36, periodicFee: 0 })
const result = ref(null)

// 货币换算（内部统一用基准币，UI 显示按当前货币）
const { symbol, toDisplay, toBase } = useCurrency()
const amountUI = computed({ get: () => toDisplay(form.value.amount), set: v => form.value.amount = toBase(v) })
const periodicFeeUI = computed({ get: () => toDisplay(form.value.periodicFee), set: v => form.value.periodicFee = toBase(v) })

const onCalc = () => {
  const r = Number(form.value.periodRatePct) / 100
  const pmt = pmtEqualPI(form.value.amount, r, form.value.periods)
  const base = scheduleEqualPI({ principal: form.value.amount, monthlyRate: r, months: form.value.periods })
  // 简单处理每期费用：叠加到 payment 与 totalPayment（不改变本金与利息拆分）
  const rows = base.rows.map(row => ({ ...row, payment: Math.round((row.payment + form.value.periodicFee) * 100)/100 }))
  const totalPayment = Math.round((base.totalPayment + form.value.periodicFee * form.value.periods) * 100)/100
  result.value = { pmt: Math.round((pmt + form.value.periodicFee) * 100)/100, totalInterest: base.totalInterest, totalPayment, rows }
}

const onReset = () => { result.value = null }

// 表格显示转为当前货币
const displayRows = computed(() => {
  if (!result.value) return []
  return result.value.rows.map(r => ({
    ...r,
    payment: Number(toDisplay(r.payment)).toFixed(2),
    principal: Number(toDisplay(r.principal)).toFixed(2),
    interest: Number(toDisplay(r.interest)).toFixed(2),
    balance: Number(toDisplay(r.balance)).toFixed(2)
  }))
})

// 显示用
const d2 = (v) => Number(toDisplay(v)).toFixed(2)

</script>
